import type {
  AddressShipmentOptional,
  Column,
  Country,
  Pricing,
  ServiceSnapshot,
  AttachmentMeta,
} from "./index";
import type { ContainerMessaging } from "./containerMessages";
import type { Handler } from "./handler";
import type { PartnerColor } from "../constants/partnerColors";

/** Which drop zone a document came from. */
/**
 * What kind of file this is.
 *
 * `plate` is not paperwork: it is the photograph of the door a container's own
 * figures were read from, filed under its own category so the evidence behind a
 * declared tare can be found beside the figure it produced. The two drop zones
 * on the attachments screen only ever write `general` or `customs` — a plate
 * photograph arrives from the reading import instead.
 */
export type AttachmentCategory = "general" | "customs" | "plate";

/** Stored attachment, tagged with the zone it was uploaded to. */
export interface ContainerOrderAttachment extends AttachmentMeta {
  category?: AttachmentCategory;
}

/**
 * A document filed against one container.
 *
 * The order's shape plus the side that filed it. Both the office and the
 * assigned driver write into one list and see all of it; `uploadedByRole` is
 * what decides whose bin icon appears, and it records what the uploader *was*
 * at upload time rather than what they are now.
 */
export interface ContainerLineAttachment
  extends Omit<ContainerOrderAttachment, "isPublic"> {
  uploadedByRole?: "system" | "driver";
}

/**
 * Container orders — batch container bookings.
 *
 * Distinct from `types/orders.ts`, which models the Dynamics 365 sales /
 * purchase order sync. A container order is created in LiveCargo and spawns N
 * child shipments of `transportMode: "CONTAINER"`.
 */

/**
 * Mirrors `backend/config/containerTypes.js` — the carrier's published ISO
 * 6346 type groups, complete. See the note on `CONTAINER_TYPE_OPTIONS` for why
 * this list is written twice, and why anything outside it is a review issue
 * rather than a new code.
 */
export type ContainerType =
  | "22GP"
  | "22VH"
  | "22UT"
  | "22UP"
  | "20PF"
  | "22PF"
  | "22PC"
  | "22RT"
  | "20TD"
  | "22TN"
  | "22TD"
  | "42GP"
  | "45GP"
  | "42UT"
  | "45UT"
  | "42UP"
  | "45UP"
  | "42PF"
  | "42PC"
  | "45PC"
  | "42RC"
  | "45RT"
  | "45RC"
  | "L5GP";

/**
 * Same vocabulary as `Shipment.direction`, so a generated shipment inherits the
 * order's direction verbatim instead of translating it. Earlier orders stored
 * the commercial wording ("export"/"import"); those were migrated, so nothing
 * reading this field has to translate anything.
 */
export type ContainerOrderDirection = "Outbound" | "Inbound";

export type ContainerOrderStatus =
  | "draft"
  | "in_progress"
  | "completed"
  | "cancelled";

export interface ContainerOrderClient {
  /** Set only when the company was picked from the Partners directory. */
  partnerId?: string | null;
  companyName?: string;
  registerationCode?: string;
  phoneNumber?: string;
  emailAddress?: string;
  address?: AddressShipmentOptional;
}

/**
 * Whether a stop is performed, and why not when it is not.
 *
 * A container move does not always start or end at a terminal — the box may
 * already be standing at the client's site, or be staying there — and until now
 * there was no way to say so: the dispatcher's only options were to invent a
 * terminal or leave the driver with a step nobody was going to take.
 *
 * The reason is not decoration. It travels to the driver's screen and onto the
 * timeline, because "no pickup" without "the box is already on site" is an
 * instruction nobody can check.
 */
export interface StopUsage {
  /** Absent on records written before stops could be turned off — those are on. */
  enabled?: boolean;
  skipReason?: string;
}

/** The reference and gate PIN every stop carries. Both optional. */
interface StopRefFields {
  /**
   * The stop's own reference — "Pickup ref", "Loading site ref" or "Delivery
   * ref" depending on the stop. Named `bookingRef` for historical reasons: it
   * was the carrier's booking reference on stop 1 before the other two stops
   * had one. Not to be confused with the order's booking number, which is
   * `contractNumber`.
   */
  bookingRef?: string;
  /** Gate / access PIN the driver quotes at this stop. */
  pin?: string;
}

/** Stops 1 and 3 — terminals, sourced from the Addresses directory. */
export interface TerminalStop extends StopRefFields, StopUsage {
  addressId?: string | null;
  companyName?: string;
  address?: AddressShipmentOptional;
}

/**
 * What an added stop is for.
 *
 * A code rather than free text, because the kind is what anyone querying this
 * afterwards asks by — which containers went through a weighbridge, which
 * customs stops ran late — and free text cannot be grouped. The label beside it
 * is what the driver reads.
 */
export const CUSTOM_STOP_KINDS = [
  "customs",
  "weighing",
  "extraLoading",
  "waypoint",
  "other",
] as const;

export type ContainerStopKind = (typeof CUSTOM_STOP_KINDS)[number];

/** Which part of the route a stop plays. */
export type ContainerStopRole =
  | "terminalPickup"
  | "loadingSite"
  | "terminalDropoff"
  | "custom";

/** The three roles a route carries exactly one of. */
export const ANCHOR_ROLES = [
  "terminalPickup",
  "loadingSite",
  "terminalDropoff",
] as const;

/** How many stops one route may hold — mirrors MAX_ROUTE_STOPS on the server. */
export const MAX_ROUTE_STOPS = 12;

/**
 * A stop the *order* adds between the three it always has.
 *
 * Anchored to an anchor rather than held in one flat ordered list: the order is
 * a template, filled in by a form whose every field is named after one of the
 * three stops. The container that is actually driven holds a flat array, because
 * a run can be any length and the driver flow walks it.
 */
export interface OrderAdditionalStop extends StopRefFields, StopUsage {
  _id?: string;
  /** Which anchor this follows. `start` is before the box is collected at all. */
  after: "start" | "terminalPickup" | "loadingSite" | "terminalDropoff";
  /** Position within that gap, so two stops in one gap keep their order. */
  sequence?: number;
  kind?: ContainerStopKind;
  label?: string;
  /** Why it is on the route, in the dispatcher's words. Shown to the driver. */
  comment?: string;
  addressId?: string | null;
  partnerId?: string | null;
  companyName?: string;
  address?: AddressShipmentOptional;
  ramp?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  date?: string | null;
  time?: string;
}

/** Stop 1 — collecting the empty container. */
export interface TerminalPickupStop extends TerminalStop {
  pickupDate?: string | null;
  pickupTime?: string;
}

export interface TerminalDropoffStop extends TerminalStop {
  deliveryDate?: string | null;
  deliveryTime?: string;
  cutOffDate?: string | null;
  cutOffTime?: string;
}

/**
 * Stop 2 — the client's loading site, sourced from the Addresses directory.
 *
 * Its slot is "on-site" rather than "loading": the same stop loads outbound and
 * unloads inbound, so naming it after one of the two would be wrong half the
 * time.
 *
 * Picked from Addresses, like the two terminals either side of it: a loading site
 * is a place a truck is sent to, and that is the directory of places. The order's
 * *client* is a separate field — see `ContainerOrderClient`, which is what
 * invoicing bills.
 */
export interface LoadingSiteStop extends StopRefFields, StopUsage {
  addressId?: string | null;
  companyName?: string;
  address?: AddressShipmentOptional;
  /** Loading bay / dock the truck reports to. */
  ramp?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  onSiteDate?: string | null;
  onSiteTime?: string;
}

export type ContainerOrderIncoterm =
  | "EXW"
  | "FCA"
  | "CPT"
  | "CIP"
  | "DAP"
  | "DPU"
  | "DDP"
  | "FAS"
  | "FOB"
  | "CFR"
  | "CIF";

/**
 * Handling flags copied onto each generated shipment's cargoHandling block.
 *
 * Each flag owns the detail field beneath it — the same pairing the shipment
 * form uses. A detail is only ever populated while its flag is on: clearing the
 * flag clears the detail, so a stored value can never outlive its explanation.
 */
export interface ContainerHandling {
  temperatureSensitive: boolean;
  temperatureRange?: string;
  adr: boolean;
  imoClass?: string;
  fragile: boolean;
  specifications?: string;
}

export interface ContainerOrderRoute {
  terminalPickup?: TerminalPickupStop;
  loadingSite?: LoadingSiteStop;
  terminalDropoff?: TerminalDropoffStop;
  /**
   * Everything the three anchors do not cover — a customs office, a certified
   * weighbridge, a second loading site, a mandated rest. Set on the order so a
   * batch that all needs the same stop is arranged once; every container
   * generated from it inherits the whole route and may then diverge.
   */
  additionalStops?: OrderAdditionalStop[];
}

/** Line counts by status, returned alongside each order in the list. */
export interface ContainerOrderProgress {
  /** Live lines, excluding cancelled ones — the denominator for progress. */
  total: number;
  /**
   * Line count per status, keyed by the status value itself — lower-case, as
   * the API sends it. Typed against the union rather than `string` so a
   * mis-cased key fails to compile instead of silently reading 0.
   */
  byStatus: Partial<Record<ContainerLineStatus, number>>;
  /** Cancelled lines, counted separately and kept out of `total`. */
  cancelled?: number;
  /**
   * How many have actually been driven — every progress status past `planned`,
   * `completed` included. See STARTED_LINE_STATUSES.
   */
  started?: number;
}

export interface ContainerOrder {
  _id: string;
  companyId: string;
  orderNumber: string;
  status: ContainerOrderStatus;
  referenceNumber?: string;
  /** The carrier's booking number — shown as "Booking number" in the UI. */
  contractNumber?: string;
  client?: ContainerOrderClient;
  direction?: ContainerOrderDirection;
  incoterm?: ContainerOrderIncoterm;
  place?: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  containerType?: ContainerType;
  shippingLine?: string;
  vesselName?: string;
  voyageNumber?: string;
  /** Bill of Lading, as the batch default — cascades onto each container. */
  blNumber?: string;
  isoCode?: string;
  defaultTareKg?: number | null;
  defaultMaxPayloadKg?: number | null;
  handling?: ContainerHandling;
  route?: ContainerOrderRoute;
  pricing?: Pricing;
  services: ServiceSnapshot[];
  costServices?: ServiceSnapshot[];
  attachments?: ContainerOrderAttachment[];
  additionalNotes?: string;
  lineCount: number;
  lineProgress?: ContainerOrderProgress;
  /**
   * The client's partner colour, on list rows only. Null when the client was
   * typed in rather than picked from Partners; `color: null` for a partner
   * with no colour chosen.
   */
  clientPartner?: { color: PartnerColor | null } | null;
  /**
   * Why Cancel is unavailable, as decided by the server — a container already
   * moving, or one whose charges have been billed. `null` means cancelling is
   * allowed.
   *
   * Shown verbatim as the menu item's tooltip. It is the server's sentence
   * because the server is the one that will refuse, and two wordings of one rule
   * is how they drift.
   */
  cancelBlockedReason?: string | null;
  /**
   * Why Remove is unavailable. A stricter question than Cancel — it asks the
   * step log as well as the statuses, and asks about every container rather than
   * only the ones a cascade would close — so it gets its own sentence.
   */
  deleteBlockedReason?: string | null;
  createdBy?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  /**
   * The dispatcher dealing with this booking.
   *
   * Distinct from `createdBy`, which never moves: one says how the record came
   * to exist (and is often the Oliver poller's reviewer, or whoever was at the
   * desk), the other says who is on it now.
   *
   * Never posted back with the form — it has its own endpoint, because the form
   * is shut on a cancelled order and a cancelled order still belongs to
   * somebody. See `containerOrderAPI.setOrderHandler`.
   */
  handlerId?: Handler | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * The prefill behind "Copy order".
 *
 * Every value the source order carried except the ones that name that one
 * booking — reference, booking number and its documents come back empty — and
 * without the identity the new order earns for itself (`_id`, `orderNumber`,
 * `status`, its counters). Partial for exactly that reason: it is an order's
 * worth of defaults, not an order.
 *
 * The source order's containers are not part of it; see the server's
 * `getContainerOrderCopyData`.
 */
export type ContainerOrderCopyData = Partial<ContainerOrder>;

/** Which order a copy came from — named on the form, never saved onto it. */
export interface ContainerOrderCopySource {
  id: string;
  orderNumber: string | null;
}

/**
 * Form payload. `attachments` carries freshly-picked `File` objects on submit
 * (multipart), mirroring how the shipment form handles uploads; already-stored
 * attachments stay on the loaded order and are managed through their own
 * endpoints.
 */
/**
 * What cancelling an order would do, as the server counts it.
 *
 * The confirmation dialog's content. Every figure here is one the client cannot
 * work out for itself: it holds one page of a batch that may run to hundreds,
 * and the question is about all of them.
 *
 * `chargeTotal` is the part that matters most. A cancelled container is not
 * chargeable — that is the rule — so every un-invoiced charge standing on a
 * container this cancellation would close stops being billable the moment it
 * goes through. Saying so in money, before the decision, is the whole reason
 * this endpoint exists.
 */
export interface ContainerOrderCancelPreview {
  orderNumber: string;
  /** The sentence to show instead of the question, or null to go ahead. */
  blocked: string | null;
  /** How many containers the cascade would close. */
  containers: number;
  /** Un-invoiced charge rows across them, and what they come to. */
  chargeCount: number;
  chargeTotal: number;
  currency: string;
  /** Board cells that would be handed back to the week. */
  releasedCells: number;
}

export interface ContainerOrderFormData {
  referenceNumber?: string;
  contractNumber?: string;
  client: ContainerOrderClient;
  direction: ContainerOrderDirection | "";
  incoterm: ContainerOrderIncoterm | "";
  place?: string;
  /**
   * `yyyy-mm-dd` while the form holds it; `null` once submitted with nothing in
   * the box. The distinction matters — "" cannot be cast to a date, and only an
   * explicit null unsets a date the order already had.
   */
  dateFrom?: string | null;
  dateTo?: string | null;
  containerType: ContainerType | "";
  shippingLine?: string;
  vesselName?: string;
  voyageNumber?: string;
  /** Bill of Lading, as the batch default — cascades onto each container. */
  blNumber?: string;
  isoCode?: string;
  defaultTareKg?: number | null;
  defaultMaxPayloadKg?: number | null;
  handling: ContainerHandling;
  route: {
    terminalPickup: TerminalPickupStop;
    loadingSite: LoadingSiteStop;
    terminalDropoff: TerminalDropoffStop;
    additionalStops: OrderAdditionalStop[];
  };
  pricing?: Pricing;
  services: ServiceSnapshot[];
  costServices?: ServiceSnapshot[];
  /** Newly picked general documents (multipart on submit). */
  attachments?: File[];
  /** Newly picked customs documents — uploaded to their own multipart field. */
  customsAttachments?: File[];
  additionalNotes?: string;
}

/**
 * The two steps every stop is made of, and what each one stamps.
 *
 * Uniform across all stops, anchors and added ones alike: a driver arrives, and
 * then the work at that stop is done. At a customs office or a weighbridge the
 * gap between the two is the whole point — dwell time at those stops is exactly
 * what gets disputed later.
 */
export const STEP_FIELDS = ["arrivedAt", "completedAt"] as const;

export type StepField = (typeof STEP_FIELDS)[number];

/**
 * What each stop's two steps are called, by role — mirroring
 * `STEP_NAMES_BY_ROLE` on the server.
 *
 * The anchors keep the names they have always had, so every event stored before
 * routes could vary still reads correctly. Added stops share one pair; which
 * stop an `arrived_stop` belongs to is said by the event's `stopId`.
 */
export const STEP_NAMES_BY_ROLE: Record<
  ContainerStopRole,
  readonly [string, string]
> = {
  terminalPickup: ["arrived_terminal_pickup", "container_collected"],
  loadingSite: ["arrived_loading_site", "container_loaded"],
  terminalDropoff: ["arrived_terminal_dropoff", "container_returned"],
  custom: ["arrived_stop", "stop_completed"],
};

export type LineStep =
  | "arrived_terminal_pickup"
  | "container_collected"
  | "arrived_loading_site"
  | "container_loaded"
  | "arrived_terminal_dropoff"
  | "container_returned"
  | "arrived_stop"
  | "stop_completed";

/**
 * Every status a container can pass through, in journey order.
 *
 * Mirrors `LINE_PROGRESS_STATUSES` on the server and is derived there the same
 * way — from the step log, never typed in — so the value that arrives always
 * describes something that actually happened.
 *
 * No longer indexable by step count: a route that varies in length cannot be,
 * because two containers with three steps done can be in quite different
 * places. The status is read off the *last recorded step* — which stop, and
 * which of its two halves — which is what `derivedLineStatus` does. The order
 * here is still an order, and the UI needs one: a filter panel, a badge legend
 * and a progress bar all lay these out as a journey.
 *
 * `cancelled` sits outside the sequence: it is an override, and reversing it
 * restores whichever progress status the container had actually reached.
 *
 * `created` is the one status not read off the log, because it is not about the
 * route: it is the container before dispatch has released it. Generating thirty
 * containers with an order is one press and is not a decision about any of
 * them; `planned` is that decision, and until it is taken the box is on no
 * driver's screen. Mirrors `isLinePlanned` on the server, which stores the
 * difference as `plannedAt`.
 */
export const LINE_PROGRESS_STATUSES = [
  "created",
  "planned",
  "at_pickup_terminal",
  "picked_up",
  "at_stop",
  "stop_completed",
  "at_loading_site",
  "loaded",
  "at_dropoff_terminal",
  "returned",
  "completed",
] as const;

export type ContainerLineStatus =
  | (typeof LINE_PROGRESS_STATUSES)[number]
  | "cancelled";

/** The two statuses a container holds before a wheel has turned. */
export const UNSTARTED_LINE_STATUSES = [
  "created",
  "planned",
] as readonly ContainerLineStatus[];

/** Statuses meaning a truck has been out with the container. */
export const STARTED_LINE_STATUSES = LINE_PROGRESS_STATUSES.filter(
  (status) => !UNSTARTED_LINE_STATUSES.includes(status),
) as readonly ContainerLineStatus[];

/**
 * One stop on a line's three-stop route.
 *
 * The whole block is copied from the order's matching stop when the container is
 * created, and is editable per container afterwards — one box in a batch may go
 * to a different terminal or be collected a day later. `arrivedAt` and
 * `completedAt` are the driver's progress and are server-owned.
 */
export interface ContainerLineStop extends StopUsage {
  companyName?: string;
  address?: {
    street?: string;
    postalCode?: string;
    state?: string;
    city?: string;
    country?: string | Country;
    coordinates?: { latitude: number; longitude: number } | null;
  };
  addressId?: string | null;
  partnerId?: string | null;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  ramp?: string;
  date?: string | null;
  time?: string;
  /** The stop's own reference — pickup / loading site / delivery ref. */
  bookingRef?: string;
  /** Gate / access PIN the driver quotes at this stop. */
  pin?: string;
  arrivedAt?: string | null;
  completedAt?: string | null;
}

/** Which of a container's three anchor stops a field belongs to. */
export type ContainerLineStopKey =
  | "terminalPickup"
  | "loadingSite"
  | "terminalDropoff";

/**
 * One stop on a container's route, as the route itself holds it.
 *
 * `ContainerLineStop` is the shape; this adds what makes an *ordered, variable*
 * route possible — which part the stop plays, and whether it is being made.
 *
 * `_id` is what everything else identifies the stop by: a route can be
 * reordered, so a position cannot. Containers written before routes could vary
 * carry their role in this field instead — the server puts it there on the way
 * out, and it is unique on a route by construction, which is all a key needs to
 * be. The migration replaces it with a real id.
 */
export interface ContainerRouteStop extends ContainerLineStop {
  _id: string;
  role: ContainerStopRole;
  /** Custom stops only — the anchors are described by their role. */
  kind?: ContainerStopKind;
  /** What the driver sees this stop called. Defaults from `kind`. */
  label?: string;
  /** Why the stop is on the route, in the dispatcher's words. */
  comment?: string;
  /**
   * Who wrote that note, and when. Both server-managed and never sent back.
   *
   * A note is an instruction from one person to another, and one with no name
   * and no time on it is one a driver cannot weigh: written this morning by the
   * dispatcher who booked the slot it is current, written three weeks ago by
   * somebody who has left it is a trap, and the two are otherwise the same grey
   * paragraph. Absent on stops written before this existed — and left absent,
   * since a note whose author was never recorded has no author to invent.
   */
  commentBy?: { _id: string; firstName?: string; lastName?: string } | null;
  commentAt?: string | null;
  /** Written from the array's own order by the server; never sent back. */
  position?: number;
  skippedAt?: string | null;
}

/**
 * One step of a container's run, as `lineSteps` derives it.
 *
 * Not a constant any more: two containers on the same order can have different
 * runs, so the steps belong to the container rather than to a table.
 */
export interface ContainerLineStepPlan {
  step: LineStep;
  field: StepField;
  /** Index into the container's `routeStops`. */
  stopIndex: number;
  stopId: string;
  role: ContainerStopRole;
}

/**
 * The parent order's identity, as the container's own page shows it.
 *
 * Read-only there: it is what the container belongs to, not something the
 * container may change. Returned alongside the container so the page does not
 * need a second request for four fields.
 */
export interface ContainerLineOrderRef {
  _id: string;
  orderNumber: string;
  referenceNumber?: string;
  /** The order's booking number — stored as `contractNumber`. */
  contractNumber?: string;
  status: ContainerOrderStatus;
  /**
   * Not displayed — it decides which leg of the run the container's CMR
   * preview treats as the consignment. See `utils/containerCmr`.
   */
  direction?: ContainerOrderDirection;
}

export interface ContainerLineEvent {
  _id: string;
  type:
    | LineStep
    | "start"
    | "complete"
    | "cancelled"
    | "reopened"
    // Dispatch releasing the container to the truck. Not a step — nobody drove
    // anywhere — but the decision every step after it depends on.
    | "planned"
    // That release taken back, from the planning board, while nothing had been
    // driven. Not a status — the container's status after it is `created`, the
    // same as before it was ever released. This is the record of *how* it got
    // back there, which clearing the stamp alone would not leave.
    | "unplanned"
    // Decisions about the run rather than points in it: a stop turned off,
    // turned back on, added, removed, or the route resequenced. Not steps —
    // nobody drove anywhere — but a route the driver was shown and then was not
    // is otherwise a change with no author and no reason.
    | "stop_skipped"
    | "stop_restored"
    | "stop_added"
    | "stop_removed"
    | "route_reordered"
    /**
     * The container changed hands in the office.
     *
     * Recorded, and — like the four stop decisions above it — deliberately not
     * drawn on the timeline. That column is the *journey*, which is why a
     * skipped stop contributes no row to it either; a change of desk even less
     * so. What the entry is for is the question asked afterwards, when
     * something went wrong in March and the field itself only holds whoever has
     * the container now. `note` carries the move in words, so the row still
     * reads once one of the two people has left and their record is gone.
     */
    | "handler_changed";
  /**
   * Which stop the event is about, when it is about one. Absent on events
   * stored before routes could vary, where the step name was the identifier and
   * could only mean one thing.
   */
  stopId?: string | null;
  /** What that stop was called at the time, so a removed stop still reads. */
  stopLabel?: string;
  timestamp: string;
  coordinates?: { latitude?: number; longitude?: number };
  note?: string;
  /**
   * Which hand recorded it — the driver from the yard, or the office from the
   * container's own form when the driver could not. Absent on events stored
   * before the two were told apart, which were all the driver's.
   */
  source?: "driver" | "system";
  /**
   * Why a `system` event carries no coordinates, one code per step. Mirrors
   * `SYSTEM_STEP_REASONS` on the server; translated under
   * `containerOrders:linePage.timeline.systemReason.*`.
   */
  reason?: string;
}

/** Packaging vocabulary a container's cargo may be counted in. */
export type ContainerCargoType =
  | "EUR"
  | "BOX"
  | "PLT"
  | "PCS"
  | "CTN"
  | "BAG"
  | "DRM"
  | "CYL"
  | "CRATE"
  | "FTL"
  | "LTL"
  | "OTHER";

/**
 * One line of what is inside a container.
 *
 * Every field is optional: a packing list is transcribed as it arrives, so a
 * row that only knows its count still has to save. Declared here rather than
 * reusing the shipment's `CargoItem` — the two modules are separate, and this
 * one has no LDM or chargeable weight (a trailer measure and an air-freight
 * billing rule respectively; neither describes a sealed box).
 */
export interface ContainerCargoItem {
  /** Present on stored rows, absent on one just added in the form. */
  _id?: string;
  quantity?: number | null;
  type?: ContainerCargoType | "";
  weightKg?: number | null;
  volumeM3?: number | null;
  /** Centimetres, as they appear on the packing list. */
  length?: number | null;
  width?: number | null;
  height?: number | null;
  description?: string;
  marks?: string;
}

/**
 * Whether a container is travelling loaded or empty.
 *
 * Stated by the office, never derived. Direction says which way the box changes
 * state over the run — outbound leaves the terminal empty and comes back full —
 * so it can never say which state the box is in right now, which is the only
 * thing a gate asks. `null` means nobody has said, and reads as a dropped row
 * rather than as "empty".
 */
export type ContainerLoadStatus = "empty" | "full";

/**
 * One physical container within an order.
 *
 * Not a Shipment: an earlier design generated container shipments from an
 * order, which forced a three-stop journey into a two-stop model. A line is its
 * own entity with its own driver flow.
 */
export interface ContainerOrderLine {
  _id: string;
  containerOrderId: string;
  orderNumber: string;
  sequence: number;
  lineNumber: string;
  status: ContainerLineStatus;

  containerType?: ContainerType;
  containerNumber?: string;
  sealNumber?: string;
  vgmKg?: number | null;
  isoCode?: string;
  tareKg?: number | null;
  maxPayloadKg?: number | null;
  cargoItems?: ContainerCargoItem[];

  /**
   * What the load weighs, when it has been weighed.
   *
   * The declaration; the packing list's own sum is the fallback when this is
   * null, so a container described row by row and never weighed reads exactly
   * as it always has. Whichever of the two is in force is what the VGM rests
   * on and what every weight rule is checked against.
   *
   * Zero is a real declaration — an empty repositioning move — and not an
   * absence, which is why it is read with `>= 0` where the other weights use
   * `> 0`.
   */
  cargoWeightKg?: number | null;

  /**
   * Loaded or empty, as declared by the office.
   *
   * Read-only to the driver, who is shown it because it is what a terminal gate
   * asks for — not so they can revise a declaration somebody else signed.
   */
  loadStatus?: ContainerLoadStatus | null;

  /**
   * The customs MRN this box is travelling under.
   *
   * Written from both ends and never frozen: a declaration is routinely issued
   * or amended at a customs office partway along the route, which is a place
   * only the driver is standing.
   */
  mrnNumber?: string;

  /**
   * How this box has to be handled.
   *
   * Inherited from the order's defaults when the container is generated, then
   * the container's own — two boxes on one booking are not necessarily the same
   * cargo. It lives here rather than being read off the order because this is
   * what the driver is given: the driver's screen never sees an order.
   */
  handling?: ContainerHandling;

  /**
   * What this container is billed for, and what it cost.
   *
   * Both inherited from the order at generation and both editable per box
   * afterwards. `services` are billable and are what an invoice is built from —
   * which is why their rows carry `invoiceId` / `invoicedAt` and become
   * immutable once billed. `costServices` are internal and are never invoiced.
   */
  services?: ServiceSnapshot[];
  costServices?: ServiceSnapshot[];

  shippingLine?: string;
  vesselName?: string;
  voyageNumber?: string;
  /** This container's Bill of Lading — inherited from the order, then its own. */
  blNumber?: string;
  bookingRef?: string;
  cutOffDate?: string | null;
  cutOffTime?: string;

  /**
   * The stops this container makes, in the order it makes them.
   *
   * The single source of truth for the run: the steps the driver is shown, the
   * timeline, the schedule badges and the derived status all come from here.
   * Always present on anything the API returns — a container written before
   * routes could vary has its three anchors stated on the way out.
   */
  routeStops?: ContainerRouteStop[];

  /**
   * How far this container's run is, measured over the stops it actually makes
   * (stops marked "Not used" are left out). Read-only: the server works it out
   * from each stop's saved location and caches it — see routeDistanceService.
   *
   * `source` is `road` for Google's driving distance, `straight` for a
   * straight-line estimate; `missingStops` counts used stops with no location,
   * which is what makes a figure incomplete. Null when the run is not measured.
   */
  routeDistance?: {
    km: number | null;
    source: "road" | "straight";
    stops: number;
    missingStops: number;
  } | null;

  /**
   * The three anchors, as the server projects them out of `routeStops`.
   *
   * A projection, not a second truth — refreshed on every save and never
   * written by anything else. They stay because a great deal of the app asks
   * about the anchors specifically and has no business walking an array to do
   * it: the batch table's route and loading-site columns, the driver list's
   * sort, the CMR's choice of consignment leg.
   */
  terminalPickup?: ContainerLineStop;
  loadingSite?: ContainerLineStop;
  terminalDropoff?: ContainerLineStop;

  driverId?: { _id: string; firstName: string; lastName: string } | string | null;
  /**
   * The dispatcher dealing with this container — the office end of it, as
   * `driverId` is the road end.
   *
   * Inherited from the order when the container was generated and its own ever
   * since. Set through its own endpoint, never the form: the form refuses a
   * completed container, and a completed container is the one still being
   * invoiced.
   */
  handlerId?: Handler | null;
  vehicleId?: { _id: string; registrationNumber: string; displayName?: string | null } | string | null;
  events?: ContainerLineEvent[];
  /**
   * When dispatch released this container to the truck.
   *
   * Null means it is still `created` — written down, not sent out, and on no
   * driver's screen. The one input to the status that is not a step, which is
   * why it is stored rather than derived; see `derivedLineStatus`.
   */
  plannedAt?: string | null;

  /**
   * Where the planning board put this container.
   *
   * Which day of its truck's week, and which position within that day. Both are
   * null together for a container the board has not placed — either never
   * planned, or assigned a truck from the container's own form, which sets no
   * position.
   *
   * Distinct from `plannedAt` above, and the distinction is the whole of the
   * board's two-press rule: this is *the plan*, and `plannedAt` is whether the
   * driver can see it yet. A container can sit in Thursday's second slot for a
   * fortnight before anybody presses send.
   *
   * Read by the driver's queue (`containerLineQueue.ts`) as the first key of
   * "what am I doing next", which is the point of planning an order at all.
   */
  planning?: {
    /** The board day, as an ISO timestamp at UTC midnight. */
    date?: string | null;
    /** 1-based position within that truck-day. */
    slot?: number | null;
  } | null;

  startedAt?: string | null;
  completedAt?: string | null;
  attachments?: ContainerLineAttachment[];
  notes?: string;

  /**
   * The container's conversation, as a counter.
   *
   * Present on every container the API returns, so a list row can draw an
   * unread badge without asking about the messages themselves. The words live
   * in their own collection and are only fetched when somebody opens the
   * thread — see `types/containerMessages.ts`.
   */
  messaging?: ContainerMessaging;

  createdAt: string;
  updatedAt: string;
}

/** The per-container fields a dispatcher may edit. */
export interface ContainerLineFormData {
  containerType?: ContainerType | "";
  containerNumber?: string;
  sealNumber?: string;
  // No VGM. It is derived server-side from the tare and the packing list, and
  // the API strips it from anything a client sends — so a key here would be a
  // field this form could fill in and never save. `ContainerOrderLine` keeps
  // its `vgmKg` because reading one back is exactly what it is for.
  isoCode?: string;
  tareKg?: number | null;
  maxPayloadKg?: number | null;
  /**
   * What the load weighs. `null` clears the declaration and hands the answer
   * back to the packing list — unlike the VGM, which is not on this form at all
   * because it is a sum rather than a measurement.
   */
  cargoWeightKg?: number | null;
  /** Loaded or empty. `""` clears it back to "not stated". */
  loadStatus?: ContainerLoadStatus | "";
  /** The customs MRN, free text. `""` clears it. */
  mrnNumber?: string;
  /**
   * The whole list, every time. The form owns it end to end, so what it sends
   * replaces what is stored — an empty array genuinely clears the cargo.
   */
  cargoItems?: ContainerCargoItem[];
  /** As above: the whole block, every time. */
  handling?: ContainerHandling;
  /**
   * The whole list, every time — the form owns both end to end, so what it
   * sends replaces what is stored and an empty array genuinely clears them.
   * Omitting the key leaves the stored rows alone, which is what keeps a save
   * that only records a step from also being a statement about the charges.
   *
   * A row that has already been invoiced may be neither removed nor rewritten;
   * the server refuses either, naming the charge.
   */
  services?: ServiceSnapshot[];
  costServices?: ServiceSnapshot[];
  shippingLine?: string;
  vesselName?: string;
  voyageNumber?: string;
  /** This container's Bill of Lading — inherited from the order, then its own. */
  blNumber?: string;
  bookingRef?: string;
  cutOffDate?: string | null;
  cutOffTime?: string;
  driverId?: string | null;
  vehicleId?: string | null;
  notes?: string;
  /**
   * The status this save asks the container to reach.
   *
   * `cancelled` closes this one box, and `planned` restores a cancelled one to
   * whatever its step log says it had actually reached. Every other value is a
   * request to record the step underneath it from the office — see
   * `LINE_STATUS_BY_STEPS`. The server refuses anything that is not exactly one
   * step forward of the log, so this is a request, never an assignment.
   */
  status?: ContainerLineStatus;
  /**
   * The whole route, in the order it is driven.
   *
   * Sent whole, every time, because that is what a route *is*: the order of the
   * stops carries as much meaning as their contents, and there is no way to say
   * "this one moved" as a patch without also saying where everything else now
   * stands. The server merges each stop over its stored self and puts the
   * driver's `arrivedAt` / `completedAt` back, so a dispatcher editing a gate
   * PIN cannot erase a recorded arrival — and refuses any change to the part of
   * the route the container has already driven.
   *
   * Omitting the key leaves the route alone, so a save that changes the seal
   * number is not also an assertion about where the container is going.
   */
  routeStops?: ContainerRouteStop[];

  /**
   * Newly picked documents, uploaded by the save that carries them.
   *
   * Only ever additions. Unlike the cargo list, the stored documents are not
   * round-tripped — the server appends what arrives and never treats a save as a
   * statement about the list as a whole, which is what lets a dispatcher save
   * this form without dropping a file the driver uploaded a minute ago.
   * Removing one goes through its own endpoint.
   */
  attachments?: File[];
  customsAttachments?: File[];
}

/**
 * "Add N containers" — the batch size is the only input.
 *
 * A per-run collection date used to sit beside it. It was dropped once the
 * order's stops grew their own pickup / on-site / delivery slots: those say the
 * same thing in the place the whole batch already inherits from, and a container
 * that really does move on another day is corrected on the container itself.
 */
export interface GenerateLinesPayload {
  count: number;
}

/**
 * One order's containers, as the store holds them.
 *
 * `loading` is per order and not a global flag, and the rows are kept while it
 * is true — a refetch must leave the table standing rather than emptying it.
 */
export interface ContainerLinesEntry {
  lines: ContainerOrderLine[];
  progress: ContainerOrderProgress | null;
  /** Rows loaded so far, across "load more" pages. */
  loadedPages: number;
  total: number;
  /**
   * How many containers match the search these rows were fetched under, or
   * `null` when they are the whole batch.
   *
   * Kept apart from `progress.total`, which always counts the batch: the panel
   * pages against this one, and against the batch total it would offer "load
   * more" over rows the filter had already excluded.
   */
  matchedTotal: number | null;
  loading: boolean;
  error: string | null;
  /**
   * Bumped when something outside the panel invalidated these rows — cancelling
   * an order now cascades to its containers, so the copies the store holds stop
   * being true. The panel watches it and re-reads; it is a signal rather than a
   * wipe so the table stays on screen while the fresh rows are on their way.
   */
  staleTick: number;
}

/**
 * The order's invoicing services that are frozen, and what froze them.
 *
 * Keyed by the order's service row `_id`; the value is the containers the
 * charge has already been invoiced on, by line number. A charge listed here
 * cannot be edited or removed on the order — the figure has left the building
 * on a document somebody is being asked to pay — so the form draws the row
 * locked rather than letting the change be made and refused.
 *
 * Computed by the server from the containers, not from the order, which is why
 * it travels beside the order rather than on it.
 */
export type ServiceInvoiceLocks = Record<string, string[]>;

export interface ContainerOrdersState {
  orders: ContainerOrder[];
  current: ContainerOrder | null;
  /**
   * The locks for `current`, as the server last reported them.
   *
   * Held beside the order rather than merged into it because it is a fact about
   * the order's *containers*: nothing on the order document knows it, and
   * anything that replaces `current` wholesale — a list row, a cancel, a reopen
   * — would otherwise silently drop it and unlock rows that are not free.
   */
  currentServiceLocks: ServiceInvoiceLocks;
  /**
   * Containers keyed by their order id.
   *
   * Keyed, not global: the orders list expands any number of rows at once and
   * every one renders the same panel. A single shared `lines` array meant two
   * open rows raced — whichever fetch landed last won, so one row displayed
   * another order's containers, under its own progress bar, with its own edit
   * and delete buttons pointed at them.
   */
  linesByOrder: Record<string, ContainerLinesEntry>;
  /**
   * Orders on the current page that the search found through one of their
   * containers rather than through anything on the order itself. The list opens
   * these rows automatically — otherwise the match is a row that looks, on its
   * face, like it has nothing to do with what was typed.
   */
  containerMatchOrderIds: string[];
  /**
   * The user's list columns, as the server last confirmed them.
   *
   * Empty until the settings request lands; the list renders its built-in
   * defaults meanwhile, so the table is never blank while columns load.
   */
  columns: Column[];
  /** Has the settings request been answered — successfully or not — this session? */
  columnsLoaded: boolean;
  columnsLoading: boolean;
  loading: boolean;
  saving: boolean;
  generating: boolean;
  error: string | null;
  pagination: { page: number; limit: number; total: number; pages: number };
}

/**
 * List query. Every facet is multi-select — the panel checkboxes accumulate,
 * the way the shipments list works — except the two ranges and the toggle.
 *
 * `clients` and `terminals` carry company *names*, not ids: both are snapshots
 * on the order, so matching by name still finds orders whose directory record
 * has since been renamed or removed.
 */
export interface ContainerOrderFilters {
  page?: number;
  limit?: number;
  search?: string;
  directions?: ContainerOrderDirection[];
  containerTypes?: ContainerType[];
  /** Range over the drop-off terminal's cut-off date. */
  cutOffFrom?: string;
  cutOffTo?: string;
  clients?: string[];
  terminals?: string[];
  /** Only orders that have no containers yet. */
  withoutLines?: boolean;
  /**
   * Narrow to one dispatcher's work, or to the unclaimed.
   *
   * A user id or `unassigned`; absent is everyone. Matched against the order's
   * own handler *or* any of its containers' — the two can legitimately differ,
   * since generation copies the handler down and each container is free to move
   * afterwards, so a dispatcher may own one box out of a booking of twelve.
   *
   * Kept out of the filter panel's `activeFilterCount` on purpose: it has its
   * own control in the toolbar and its own visible state, and counting it among
   * the panel's facets would put a badge on a dropdown that does not contain
   * it, with a Clear filters button that does not clear it.
   */
  handler?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export type { Country };
